import React, { useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import OutsideClickDetector from "@components-shared/OutsideClickDetector";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import MessageContainerHandler from "./MessageContainerHandler";

import { useRootAppStore } from "@hooks";

import { ActiveSharedBetSlip, ChatMessageType } from "@data-types/chat";
import { LoginResponseUser } from "@api-types/user/LoginResponseDto";

export default observer(function MessageContainer(props: {
	noReport?: boolean;
	noReply?: boolean;
	support?: boolean;
	message: Amity.Message &
		Amity.RawMessage & {
			parentId?: string;
			data: {
				text?: string;
				sharedSlip: ActiveSharedBetSlip;
				messageType: ChatMessageType.REPLY;
				replyingCreatorId: string;
				replyingMessage: string;
				betSlipData: ActiveSharedBetSlip;
			};
			metadata: {
				isLink?: boolean;
				isMultiOffer?: boolean;
			};
		};
}) {
	const { message, noReply } = props;

	const [isUserInfoOpen, setIsUserInfoOpen] = useState(false);
	const rootStore = useRootAppStore();
	const isUserCreator =
		props.message.creatorId === rootStore.userAuthStore.user?.chatName;

	if (props.support && isUserCreator) {
		return (
			<ErrorBoundaryWithPropagation
				componentName={"Desktop/MessageContainer.tsx"}
			>
				<div
					id={message.uniqueId}
					className={classNames("chat__row", {
						"chat--support__row": props.support,
						"chat__row--highlighted": isUserInfoOpen,
					})}
				>
					<div
						className={classNames("chat__row__inner", {
							"chat__bubble chat__bubble--right chat--support__bubble":
								props.support,
						})}
					>
						<PreMessageContent
							noReport={props.noReport}
							isUserInfoOpen={isUserInfoOpen}
							setIsUserInfoOpen={setIsUserInfoOpen}
							message={message}
							user={rootStore.userAuthStore.user}
							isReversed
						/>

						<MessageContainerHandler
							message={message}
							support={props.support}
						/>
					</div>

					{!noReply && (
						<div
							className="chat__btn--reply btn btn--square btn--square--xsml btn--ghost"
							onClick={() => {
								document
									.getElementById("chat-textarea")
									?.focus();
								rootStore.chatViewStore.setSelectedMessageToReplyOn(
									props.message
								);
							}}
						>
							<i className="u-icon u-icon--sml u-icon--reply u-color--text--primary" />
						</div>
					)}
				</div>
			</ErrorBoundaryWithPropagation>
		);
	}

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Desktop/MessageContainer.tsx"}
		>
			<div
				id={message.uniqueId}
				className={classNames("chat__row", {
					"chat--support__row": props.support,
					"chat__row--highlighted": isUserInfoOpen,
				})}
			>
				<div
					className={classNames("chat__row__inner", {
						"chat__bubble chat__bubble--left chat--support__bubble":
							props.support,
					})}
				>
					<PreMessageContent
						noReport={props.noReport}
						isUserInfoOpen={isUserInfoOpen}
						setIsUserInfoOpen={setIsUserInfoOpen}
						message={message}
						user={rootStore.userAuthStore.user}
					/>

					<MessageContainerHandler
						message={message}
						support={props.support}
					/>
				</div>

				{!noReply && (
					<div
						className="chat__btn--reply btn btn--square btn--square--xsml btn--ghost"
						onClick={() => {
							document.getElementById("chat-textarea")?.focus();
							rootStore.chatViewStore.setSelectedMessageToReplyOn(
								props.message
							);
						}}
					>
						<i className="u-icon u-icon--sml u-icon--reply u-color--text--primary" />
					</div>
				)}
			</div>
		</ErrorBoundaryWithPropagation>
	);
});

function PreMessageContent(props: {
	noReport?: boolean;
	isUserInfoOpen: boolean;
	setIsUserInfoOpen: React.Dispatch<React.SetStateAction<boolean>>;
	message: Amity.Message & {
		data: {
			text?: string;
			sharedSlip?: ActiveSharedBetSlip;
		};
		metadata: {
			isLink?: boolean;
		};
	};
	user: LoginResponseUser | null;
	isReversed?: boolean;
}) {
	const { t } = useTranslation();
	const { chatViewStore } = useRootAppStore();

	const isUserCreator = props.message.creatorId === props.user?.chatName;

	const openUserInfo = () => {
		if (!isUserCreator) {
			props.setIsUserInfoOpen(true);
		}
	};

	return (
		<>
			<ErrorBoundaryWithPropagation
				componentName={"Desktop/MessageContainer.tsx/PreMessageContent"}
			>
				{!props.noReport && props.isUserInfoOpen && (
					<OutsideClickDetector
						onOutsideClick={() => props.setIsUserInfoOpen(false)}
						wrapperClasses="chat__flag"
					>
						<div
							className="chat__flag__inner btn btn--base btn--primary--inverted btn--icon u-color--sent--danger"
							onClick={async () => {
								props.setIsUserInfoOpen(false);
								await chatViewStore.chatChannelHandler.checkIfMessageShouldBeFlagged(
									props.message.uniqueId,
									props.message.creatorId
								);
							}}
						>
							<i className="u-icon u-icon--med u-icon--alert-circle--bold u-color--sent--danger" />
							<span className="u-type--sml">
								{t("CHAT.REPORT_MESSAGE")}
							</span>
						</div>
					</OutsideClickDetector>
				)}

				<span
					className={classNames("chat__sender", {
						"is-visible": !props.noReport && !isUserCreator,
					})}
					onClick={() => {
						!props.noReport && openUserInfo();
					}}
				>
					<span
						id={props.message.uniqueId}
						className="chat__sender__timestamp u-mar--right--xtny u-color--text--tertiary"
					>
						{new Date(props.message.createdAt)
							.getHours()
							.toString()
							.padStart(2, "0") +
							":" +
							new Date(props.message.createdAt)
								.getMinutes()
								.toString()
								.padStart(2, "0")}
					</span>

					<span
						className={classNames("chat__sender__name", {
							chat__sender__me: isUserCreator,
						})}
					>
						{props.message.creatorId}:
					</span>
				</span>
			</ErrorBoundaryWithPropagation>
		</>
	);
}
